import { Vec3 } from '../core/math';
import { AMMO, type AmmoId } from '../data/classes';
import { cannonMount, type CannonSpec } from '../data/ship';

// Laying a gun on a point of the sea, or on a height above it (a ship's
// hull or rigging there). A ball is flown exactly as Ballistics flies it
// (drag against the moving air, gravity, the ship's own way carried into the
// shot) and the bore direction that brings it onto the mark is found by a few
// Newton steps, warm-started from the last solution so a gun kept on a
// moving mark costs one flight a step.

const G = 9.81;
/** Shot is only followed this long (Ballistics drops it after). */
export const SHOT_LIFE = 14;
/** The step Ballistics flies shot at (the sim's step), s. */
export const FLIGHT_DT = 1 / 60;

/** Where on a ship the guns are laid: her waterline, her topsides, or her rigging. */
export type AimPart = 'waterline' | 'hull' | 'rigging';
export const AIM_PARTS: readonly AimPart[] = ['waterline', 'hull', 'rigging'];

/** How high above the sea to lay for that part of a ship of this height scale, m. */
export function aimHeight(part: AimPart, sy = 1): number {
  return part === 'rigging' ? 14 * sy : part === 'hull' ? 2.8 * sy : 0.5;
}

/** Muzzle velocity of a gun with this charge (the short quarterdeck pieces throw slower). */
export function muzzleSpeed(spec: CannonSpec, ammo: AmmoId): number {
  return AMMO[ammo].speed * (spec.deck === 'main' ? 1 : 0.88);
}

/** Drag coefficient of a ball of this charge and weight (per metre), as Ballistics has it. */
export function shotDrag(ammo: AmmoId, weight: number): number {
  return AMMO[ammo].drag / Math.cbrt(weight);
}

/** Where a gun's muzzle is when run out, design space. */
export function muzzleDesign(spec: CannonSpec, out: Vec3): Vec3 {
  const m = cannonMount(spec);
  return out.set(m.x, m.y + 0.05, spec.side * (Math.abs(m.z) + spec.length * 0.6));
}

/**
 * Where a ball comes down through sea level (y = 0), or, flown to an upright
 * plane, where it passes through that plane (or the sea, if it falls short).
 */
export interface Fall {
  x: number;
  z: number;
  /** Height there, m (0 on the sea). */
  y: number;
  /** It reached the plane (rather than the sea short of it). */
  cross: boolean;
  /** Time of flight, s. */
  t: number;
  /** Descent per metre run as it arrives: how far a small error in height carries it on. */
  slope: number;
  /** It came down at all while it was still being followed. */
  ok: boolean;
}

export function makeFall(): Fall {
  return { x: 0, z: 0, y: 0, cross: false, t: 0, slope: 0, ok: false };
}

/** An upright plane across the line of fire: a horizontal direction from the muzzle and the distance along it, m. */
export interface Plane {
  ux: number;
  uz: number;
  R: number;
}

/**
 * Fly a ball from `p` with velocity `v` through air moving at (windX, windZ),
 * in steps of `dt`, exactly as Ballistics does, until it comes down through
 * sea level - or, given `plane`, reaches that plane first. `each` is given
 * every point of the path (and the last one).
 */
export function flyShot(p: Vec3, v: Vec3, k: number, windX: number, windZ: number, dt: number, out: Fall,
  each?: (x: number, y: number, z: number) => void, plane?: Plane): Fall {
  let px = p.x, py = p.y, pz = p.z;
  let vx = v.x, vy = v.y, vz = v.z;
  let run = 0;
  each?.(px, py, pz);
  out.ok = out.cross = false;
  out.y = 0;
  for (let t = dt; t <= SHOT_LIFE; t += dt) {
    const ax = vx - windX, az = vz - windZ;
    const f = k * Math.hypot(ax, vy, az) * dt;
    vx -= ax * f;
    vy -= vy * f + G * dt;
    vz -= az * f;
    const qx = px, qy = py, qz = pz;
    px += vx * dt;
    py += vy * dt;
    pz += vz * dt;
    if (py <= 0 && vy < 0) {
      // Where between the last two points it met the sea.
      const u = qy / Math.max(1e-9, qy - py);
      out.x = qx + (px - qx) * u;
      out.z = qz + (pz - qz) * u;
      out.t = t - dt + u * dt;
      out.slope = -vy / Math.max(1e-6, Math.hypot(vx, vz));
      out.ok = true;
      each?.(out.x, 0, out.z);
      return out;
    }
    if (plane) {
      const was = run;
      run = (px - p.x) * plane.ux + (pz - p.z) * plane.uz;
      if (run >= plane.R && run > was) {
        const u = (plane.R - was) / (run - was);
        out.x = qx + (px - qx) * u;
        out.y = qy + (py - qy) * u;
        out.z = qz + (pz - qz) * u;
        out.t = t - dt + u * dt;
        out.slope = -vy / Math.max(1e-6, Math.hypot(vx, vz));
        out.ok = out.cross = true;
        each?.(out.x, out.y, out.z);
        return out;
      }
    }
    each?.(px, py, pz);
  }
  out.x = px;
  out.z = pz;
  out.t = SHOT_LIFE;
  out.slope = 0;
  return out;
}

/** A gun's lay in the world: the bore's azimuth (atan2(z, x)) and elevation above the horizon, rad. */
export interface Lay {
  az: number;
  el: number;
  /** Time of flight to the mark, s. */
  t: number;
  /** Descent per metre run as the ball arrives there. */
  slope: number;
  /** How far off the mark the ball would still come down, m (NaN: not solved yet). */
  miss: number;
}

export function makeLay(): Lay {
  return { az: NaN, el: NaN, t: 0, slope: 0, miss: NaN };
}

const fall = makeFall();
const vel = new Vec3();
const plane: Plane = { ux: 0, uz: 0, R: 0 };

/**
 * Bring `lay` (a warm start, or NaN) onto the mark (tx, ty, tz): a ball fired
 * from `p` at `speed` along the bore, carried by the ship's velocity `vShip`,
 * should come down on the sea there (ty 0), or pass that high over it.
 * Elevation from how far over or short it falls (scaled by the slope of the
 * fall), or how far above or below the mark it passes; azimuth from how far
 * to one side. Elevation is kept to `elMax` (and above -0.5): past it the
 * solution is only the limit, and `miss` says by how much.
 */
export function solveLay(lay: Lay, p: Vec3, vShip: Vec3, speed: number, k: number, tx: number, tz: number,
  windX: number, windZ: number, dt: number, iterations = 4, elMax = 0.6, ty = 0): Lay {
  const dx = tx - p.x, dz = tz - p.z;
  const R = Math.max(1, Math.hypot(dx, dz));
  const ux = dx / R, uz = dz / R;
  const high = ty > 0;
  Object.assign(plane, { ux, uz, R });
  if (!Number.isFinite(lay.az) || !Number.isFinite(lay.el)) {
    // Flat fire with drag: time to the mark, and the drop over it.
    const t = (Math.exp(Math.min(k * R, 20)) - 1) / (k * speed);
    lay.az = Math.atan2(dz, dx);
    lay.el = Math.atan2(0.5 * G * t * t - p.y + ty, R);
  }
  for (let i = 0; i < iterations; i++) {
    const ce = Math.cos(lay.el);
    vel.set(Math.cos(lay.az) * ce * speed + vShip.x, Math.sin(lay.el) * speed + vShip.y, Math.sin(lay.az) * ce * speed + vShip.z);
    flyShot(p, vel, k, windX, windZ, dt, fall, undefined, high ? plane : undefined);
    const lx = fall.x - p.x, lz = fall.z - p.z;
    const along = lx * ux + lz * uz;
    const across = -lx * uz + lz * ux;
    if (!fall.ok) {
      // Still aloft when it's given up on: far beyond anything a gun can reach.
      lay.el = Math.max(-0.5, lay.el - 0.1);
      lay.miss = Infinity;
      continue;
    }
    lay.t = fall.t;
    lay.slope = fall.slope;
    let de: number;
    if (fall.cross) {
      // Through the plane over the mark: raising the bore by d lifts the
      // path there by about R d / cos^2.
      const low = ty - fall.y;
      lay.miss = Math.hypot(low, across);
      if (lay.miss < 0.15) break;
      de = (low * ce * ce) / R;
    } else {
      // Into the sea (short of the plane, if there is one): raising the bore
      // by d lifts the path at the mark by about along * d; the ball then
      // carries on (or falls short) by that over the slope - and it has to
      // clear the height wanted there as well.
      const short = R - along;
      lay.miss = Math.hypot(short, across) + ty;
      if (lay.miss < 0.15) break;
      de = (short * Math.max(fall.slope, 1e-3) + ty) / Math.max(along, 1);
    }
    de = Math.max(-0.08, Math.min(0.08, de));
    const el = Math.max(-0.5, Math.min(elMax, lay.el + de));
    const pinned = el === lay.el && de !== 0;
    lay.el = el;
    lay.az -= across / R;
    if (pinned && Math.abs(across) < 0.15) break;
  }
  return lay;
}
